// Add / edit / delete the VPAs (UPI ids) of one customer from the admin side.
// Each action is shown only with its own privilege (set per designation in Designation Privileges):
//   customers.all.addVpa, customers.all.editVpa, customers.all.deleteVpa
import React, { useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';
import { API_BASE_URL } from '../constants/constants';
import { usePermissions } from '../context/PermissionContext';

const VPA_RE = /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z0-9]{2,64}$/;

const CustomerVpaManagerDialog = ({ open, onClose, customer, onUpdated, permScopes = ['customers.all'] }) => {
  const { can: canKey } = usePermissions();
  // an action is allowed when the user holds it on any of the given pages (e.g. All Customers / Customers Logins Manage)
  const can = (key) => permScopes.some((scope) => canKey(`${scope}.${key.split('.').pop()}`));
  const [mode, setMode] = useState(null); // null | 'add' | { edit: vpaObj } | { del: vpaObj }
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const vpas = (customer && customer.customerVPA) || [];
  const customerId = customer && (customer.customerId || customer._id);
  const headers = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } });

  const reset = () => { setMode(null); setValue(''); setError(''); };
  const close = () => { reset(); onClose(); };

  const submit = async () => {
    setError('');
    const vpa = value.trim();
    const isEdit = mode && mode.edit;
    if (mode === 'add' || isEdit) {
      if (!VPA_RE.test(vpa)) return setError('Enter a valid UPI ID, e.g. name@bank');
      if (vpas.some((v) => v.vpa.toLowerCase() === vpa.toLowerCase() && (!isEdit || v._id !== mode.edit._id))) {
        return setError('This customer already has this VPA.');
      }
    }
    setBusy(true);
    try {
      let res;
      if (mode === 'add') res = await axios.post(`${API_BASE_URL}/customerProfiles/${customerId}/vpa`, { vpa }, headers());
      else if (isEdit) res = await axios.put(`${API_BASE_URL}/customerProfiles/${customerId}/vpa/${mode.edit._id}`, { vpa }, headers());
      else res = await axios.delete(`${API_BASE_URL}/customerProfiles/${customerId}/vpa/${mode.del._id}`, headers());
      if (res.data && res.data.customerVPA) onUpdated(res.data.customerVPA);
      reset();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || 'Action failed. Please try again.');
    }
    setBusy(false);
  };

  const deleting = mode && mode.del;
  return (
    <Dialog open={open} onClose={busy ? undefined : close} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 800 }}>
        VPAs of {customer ? customer.customerName : ''}
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {vpas.length === 0 && <Typography color="text.secondary" align="center" sx={{ py: 2 }}>No VPAs found for this customer.</Typography>}
        <Stack spacing={1}>
          {vpas.map((v) => (
            <Box key={v._id} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#f8fafc' }}>
              <Chip label={v.vpa} color="primary" variant="outlined" sx={{ fontWeight: 600, flex: 1, justifyContent: 'flex-start' }} />
              {can('customers.all.editVpa') && (
                <Tooltip title="Edit VPA"><IconButton size="small" color="primary" onClick={() => { setError(''); setValue(v.vpa); setMode({ edit: v }); }}><EditIcon fontSize="small" /></IconButton></Tooltip>
              )}
              {can('customers.all.deleteVpa') && (
                <Tooltip title="Delete VPA"><IconButton size="small" color="error" onClick={() => { setError(''); setMode({ del: v }); }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
              )}
            </Box>
          ))}
        </Stack>

        {(mode === 'add' || (mode && mode.edit)) && (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth autoFocus size="small" label={mode === 'add' ? 'New VPA / UPI ID' : 'Edit VPA / UPI ID'}
              placeholder="name@bank" value={value} onChange={(e) => setValue(e.target.value)} disabled={busy}
            />
          </Box>
        )}
        {deleting && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Delete <strong>{mode.del.vpa}</strong>? This cannot be undone.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        {mode ? (
          <>
            <Button onClick={reset} disabled={busy}>Cancel</Button>
            <Button
              variant="contained" color={deleting ? 'error' : 'primary'} onClick={submit} disabled={busy}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {deleting ? 'Delete' : mode === 'add' ? 'Add VPA' : 'Save'}
            </Button>
          </>
        ) : (
          <>
            {can('customers.all.addVpa') && (
              <Button startIcon={<AddIcon />} onClick={() => { setError(''); setValue(''); setMode('add'); }}>Add VPA</Button>
            )}
            <Box sx={{ flex: 1 }} />
            <Button onClick={close}>Close</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CustomerVpaManagerDialog;
